/**
 * Comparison Period Selector
 *
 * Styled for Monutrack dark theme.
 * Allows user to select a comparison period:
 * - None (no comparison)
 * - Previous Period (same duration, immediately before)
 * - Same Period Last Year (YoY comparison)
 */

export type ComparisonMode = 'none' | 'previous_period' | 'same_period_last_year';

interface ComparisonSelectorProps {
  value: ComparisonMode;
  onChange: (mode: ComparisonMode) => void;
  disabled?: boolean;
}

const OPTIONS: { value: ComparisonMode; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'Nenhum',
    description: 'Sem comparacao',
  },
  {
    value: 'previous_period',
    label: 'Periodo Anterior',
    description: 'Periodo imediatamente antes',
  },
  {
    value: 'same_period_last_year',
    label: 'Mesmo Periodo Ano Ant.',
    description: 'Comparar com ano passado',
  },
];

export function ComparisonSelector({
  value,
  onChange,
  disabled = false,
}: ComparisonSelectorProps) {
  return (
    <div className="flex gap-2 flex-wrap">
      {OPTIONS.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            className={`
              comparison-button px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
              focus:outline-none focus:ring-2 focus:ring-[#D2FA52]
              ${
                isActive
                  ? 'border-2 border-[#D2FA52] bg-[#D2FA52]/20 text-[#D2FA52] font-semibold'
                  : disabled
                    ? 'border border-[#3A3D44] bg-[#2A2D34] text-[#5A5D66] cursor-not-allowed opacity-60'
                    : 'border border-[#3A3D44] bg-[#2A2D34] text-[#838998] hover:bg-[#3A3D44] hover:text-[#D2FA52] cursor-pointer'
              }
            `}
            title={option.description}
          >
            {option.value === 'none' ? '○' : '●'} {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Calculate comparison date range based on mode
 */
export function calculateComparisonRange(
  startDate: string,
  endDate: string,
  mode: ComparisonMode
): { startDate: string; endDate: string } | null {
  if (mode === 'none') return null;

  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');

  // Calculate duration
  const durationMs = end.getTime() - start.getTime();

  if (mode === 'previous_period') {
    // Move both dates back by the duration
    const compEnd = new Date(start.getTime() - 1000 * 60 * 60 * 24); // day before start
    const compStart = new Date(compEnd.getTime() - durationMs);

    return {
      startDate: compStart.toISOString().split('T')[0],
      endDate: compEnd.toISOString().split('T')[0],
    };
  }

  if (mode === 'same_period_last_year') {
    // Move both dates back by 1 year
    const compStart = new Date(start);
    compStart.setFullYear(compStart.getFullYear() - 1);

    const compEnd = new Date(end);
    compEnd.setFullYear(compEnd.getFullYear() - 1);

    return {
      startDate: compStart.toISOString().split('T')[0],
      endDate: compEnd.toISOString().split('T')[0],
    };
  }

  return null;
}

export default ComparisonSelector;
